import { type FC, useEffect, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { PieChartEmptyCard } from "../datamintEmptyStates";
import DatamintDashboard from "./DatamintDashboard";
import { DashboardReceivablesPanel } from "./DashboardReceivablesPanel";
import { DashboardTabs } from "./DashboardTabs";
import {
  getAvailableDashboardTabs,
  resolveDashboardTab,
  shouldShowDashboardTabs,
  type DashboardTab,
} from "./dashboardUtils";

const STORAGE_KEY = "dashboards-active-tab";

function readStoredTab(): DashboardTab | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "cashflow" || stored === "receivables") return stored;
  } catch {
    /* ignore */
  }
  return null;
}

function persistTab(tab: DashboardTab): void {
  try {
    localStorage.setItem(STORAGE_KEY, tab);
  } catch {
    /* ignore */
  }
}

function renderDashboardPanel(tab: DashboardTab) {
  if (tab === "cashflow") {
    return <DatamintDashboard />;
  }

  return (
    <div className="flex flex-1 flex-col overflow-auto bg-slate-50/60">
      <div className="w-full min-w-0 flex-1 space-y-6 py-6">
        <DashboardReceivablesPanel />
      </div>
    </div>
  );
}

const Dashboards: FC = () => {
  const { scopes } = useAuth();
  const availableTabs = getAvailableDashboardTabs(scopes);

  const [activeTab, setActiveTab] = useState<DashboardTab>(() =>
    resolveDashboardTab(scopes, readStoredTab()),
  );

  useEffect(() => {
    setActiveTab(resolveDashboardTab(scopes, readStoredTab()));
  }, [scopes]);

  const handleTabChange = (tab: DashboardTab) => {
    setActiveTab(tab);
    persistTab(tab);
  };

  if (availableTabs.length === 0) {
    return (
      <div className="flex min-h-0 w-full flex-1 flex-col justify-center px-6 py-8">
        <PieChartEmptyCard title="Dashboard" ctaTo="/files-integrations" />
      </div>
    );
  }

  if (!shouldShowDashboardTabs(availableTabs)) {
    return (
      <div className="flex min-h-0 w-full flex-1 flex-col">
        {renderDashboardPanel(availableTabs[0])}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <div className="shrink-0 border-b border-slate-200 bg-white px-6 py-3">
        <DashboardTabs
          tabs={availableTabs}
          active={activeTab}
          onChange={handleTabChange}
        />
      </div>

      {renderDashboardPanel(activeTab)}
    </div>
  );
};

export default Dashboards;
